import {
  RETAIL_VISIBILITY_CONFIG,
  type AccountRetailer,
  type RetailEntity,
  type RetailVisibilityAccountConfig,
} from "./retailVisibilityConfig";

export type RetailVisibilityStatus = "not_run" | "ready";
export type RetailSignalStatus = "Strong" | "Moderate" | "Weak" | "Absent";

type RetailResultRow = {
  category?: string | null;
  prompt_score?: number | null;
  visibility_signal?: string | null;
  brand_mentions?: unknown;
  llm_response_text?: string | null;
  prompt_type?: string | null;
};

export type RetailVisibilitySummary = {
  status: RetailVisibilityStatus;
  score: number;
  headline: string;
  narrative: string;
  recommendedAction: string;

  customerBrandMentionRate: number;
  priorityRetailerMentionRate: number;
  ownedChannelMentionRate: number;

  priorityChannels: Array<{
    name: string;
    role: string;
    mentions: number;
    visibilityRate: number;
    interpretation: RetailSignalStatus;
  }>;

  channelAlternatives: Array<{
    name: string;
    mentions: number;
    visibilityRate: number;
  }>;

  topCompetitors: Array<{
    name: string;
    mentions: number;
    visibilityRate: number;
  }>;

  productInsights: Array<{
    label: string;
    status: "Strong" | "Moderate" | "Weak";
    detail: string;
  }>;
};

function normalize(value: string): string {
  return value.toLowerCase().replace(/[’]/g, "'").trim();
}

function getRowText(row: RetailResultRow): string {
  const brandMentions = Array.isArray(row.brand_mentions)
    ? row.brand_mentions.join(" ")
    : "";

  return [
    row.category,
    row.visibility_signal,
    row.llm_response_text,
    brandMentions,
  ]
    .filter(Boolean)
    .join(" ");
}

function includesAny(text: string, terms: string[]): boolean {
  const normalizedText = normalize(text);

  return terms
    .filter(Boolean)
    .some((term) => normalizedText.includes(normalize(term)));
}

function countRowsWithTerms(rows: RetailResultRow[], terms: string[]): number {
  return rows.filter((row) => includesAny(getRowText(row), terms)).length;
}

function countTerm(rows: RetailResultRow[], terms: string[]): number {
  return countRowsWithTerms(rows, terms);
}

function pct(count: number, total: number): number {
  if (!total) return 0;
  return Math.round((count / total) * 100);
}

function averageScore(rows: RetailResultRow[]): number {
  if (!rows.length) return 0;

  const total = rows.reduce((sum, row) => sum + Number(row.prompt_score || 0), 0);
  return total / rows.length;
}

function interpretation(
  mentions: number,
  totalRows: number
): RetailSignalStatus {
  const rate = totalRows ? mentions / totalRows : 0;

  if (rate >= 0.45) return "Strong";
  if (rate >= 0.25) return "Moderate";
  if (rate > 0) return "Weak";
  return "Absent";
}

function sortByMentions<T extends { mentions: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => b.mentions - a.mentions);
}

function aliasesForRetailer(retailer: AccountRetailer): string[] {
  return [
    retailer.retailerName,
    retailer.retailerDomain ?? "",
    ...(retailer.aliases ?? []),
  ].filter(Boolean);
}

function countEntity(rows: RetailResultRow[], entity: RetailEntity): number {
  return countTerm(rows, entity.aliases);
}

function roleLabel(role: string, isOwnedChannel?: boolean): string {
  if (isOwnedChannel) return "Owned ecommerce / brand channel";

  return role
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatList(items: string[], fallback: string): string {
  const cleanedItems = items.filter(Boolean);

  if (!cleanedItems.length) return fallback;
  if (cleanedItems.length === 1) return cleanedItems[0];
  if (cleanedItems.length === 2) return `${cleanedItems[0]} and ${cleanedItems[1]}`;

  return `${cleanedItems.slice(0, -1).join(", ")}, and ${
    cleanedItems[cleanedItems.length - 1]
  }`;
}

function getConfig(
  configKey: keyof typeof RETAIL_VISIBILITY_CONFIG = "forney"
): RetailVisibilityAccountConfig {
  return RETAIL_VISIBILITY_CONFIG[configKey] ?? RETAIL_VISIBILITY_CONFIG.forney;
}

function getEmptySummary(): RetailVisibilitySummary {
  return {
    status: "not_run",
    score: 0,
    headline: "Retail visibility has not been analyzed yet.",
    narrative:
      "This account is configured for AI Retail Visibility, but no retail prompt results are available for the selected run.",
    recommendedAction:
      "Run a retail visibility analysis to identify channel, retailer, and product-level opportunities.",
    customerBrandMentionRate: 0,
    priorityRetailerMentionRate: 0,
    ownedChannelMentionRate: 0,
    priorityChannels: [],
    channelAlternatives: [],
    topCompetitors: [],
    productInsights: [],
  };
}

export function buildRetailVisibilitySummary(
  rows: RetailResultRow[],
  configKey: keyof typeof RETAIL_VISIBILITY_CONFIG = "forney"
): RetailVisibilitySummary {
  if (!rows.length) {
    return getEmptySummary();
  }

  const config = getConfig(configKey);
  const totalRows = rows.length;
  const brandName = config.accountName;

  const customerAliases = config.customerBrandNames ?? [brandName];

  const organicRetailerEntities = config.organicRetailerEntities ?? [];
  const competitorEntities = config.competitorEntities ?? [];
  const productSegments = config.productSegments ?? [];

  const ownedRetailers = config.priorityRetailers.filter(
    (retailer) => retailer.isOwnedChannel
  );

  const customerBrandMentions = countTerm(rows, customerAliases);

  const ownedCommerceMentions = countTerm(
    rows,
    ownedRetailers.flatMap(aliasesForRetailer)
  );

  const priorityRetailerMentions = countTerm(
    rows,
    config.priorityRetailers.flatMap(aliasesForRetailer)
  );

  const customerBrandMentionRate = pct(customerBrandMentions, totalRows);
  const priorityRetailerMentionRate = pct(priorityRetailerMentions, totalRows);
  const ownedChannelMentionRate = pct(ownedCommerceMentions, totalRows);

  const categoryChannelRows = rows.filter(
    (row) => row.category === "retail_category_channel"
  );

  const priorityRetailerRows = rows.filter(
    (row) => row.category === "retail_priority_retailer"
  );

  const ownedRows = rows.filter(
    (row) => row.category === "retail_owned_ecommerce"
  );

  const bestProductRows = rows.filter(
    (row) => row.category === "retail_best_product"
  );

  const categoryChannelAverage = averageScore(categoryChannelRows);
  const priorityRetailerAverage = averageScore(priorityRetailerRows);
  const ownedAverage = averageScore(ownedRows);
  const bestProductAverage = averageScore(bestProductRows);

  const score = Math.round(
    categoryChannelAverage * 3.5 +
      priorityRetailerAverage * 3 +
      bestProductAverage * 2.5 +
      ownedAverage * 1
  );

  const priorityChannels = sortByMentions(
    config.priorityRetailers.map((retailer) => {
      const mentions = countTerm(rows, aliasesForRetailer(retailer));

      return {
        name: retailer.retailerName,
        role: roleLabel(retailer.channelRole, retailer.isOwnedChannel),
        mentions,
        visibilityRate: pct(mentions, totalRows),
        interpretation: interpretation(mentions, totalRows),
      };
    })
  );

  const channelAlternatives = sortByMentions(
    organicRetailerEntities
      .map((entity) => {
        const mentions = countEntity(rows, entity);

        return {
          name: entity.canonicalName,
          mentions,
          visibilityRate: pct(mentions, totalRows),
        };
      })
      .filter((item) => item.mentions > 0)
  ).slice(0, 8);

  const topCompetitors = sortByMentions(
    competitorEntities
      .map((entity) => {
        const mentions = countEntity(rows, entity);

        return {
          name: entity.canonicalName,
          mentions,
          visibilityRate: pct(mentions, totalRows),
        };
      })
      .filter((item) => item.mentions > 0)
  ).slice(0, 8);

  const productInsights =
    productSegments.length > 0
      ? productSegments.map((segment) => {
          const segmentRows = rows.filter((row) =>
            includesAny(getRowText(row), segment.aliases)
          );

          const brandMentionsInSegment = countTerm(segmentRows, customerAliases);
          const segmentRate = pct(brandMentionsInSegment, Math.max(segmentRows.length, 1));

          const status: "Strong" | "Moderate" | "Weak" =
            segmentRate >= 50 ? "Strong" : segmentRate >= 25 ? "Moderate" : "Weak";

          return {
            label: segment.label,
            status,
            detail:
              segmentRows.length > 0
                ? `${brandName} appeared in ${segmentRate}% of matched retail prompts for this segment.`
                : `No clear matched retail prompts were found for this segment in the selected run.`,
          };
        })
      : [
          {
            label: "Product visibility",
            status: "Moderate" as const,
            detail:
              "Retail prompt results were found. Add product segment configuration to show segment-level visibility.",
          },
        ];

  const topOrganicRetailers = channelAlternatives
    .slice(0, 4)
    .map((channel) => channel.name);

  const topCompetitorNames = topCompetitors
    .slice(0, 5)
    .map((competitor) => competitor.name);

  const weakerPriorityChannels = priorityChannels
    .filter(
      (channel) =>
        channel.interpretation === "Weak" ||
        channel.interpretation === "Absent"
    )
    .slice(0, 3)
    .map((channel) => channel.name);

  const priorityChannelNames = config.priorityRetailers
    .slice()
    .sort((a, b) => a.priorityRank - b.priorityRank)
    .map((retailer) => retailer.retailerName);

  const headline = `${brandName} is visible in AI retail results, but broad category demand is not fully aligned to priority channels.`;

  const narrative = `${brandName} appears in ${customerBrandMentionRate}% of retail shopping prompts. The bigger opportunity is channel alignment: broad category-shopping prompts often route buyers toward ${formatList(
    topOrganicRetailers,
    "organic retail alternatives"
  )} before consistently connecting demand to ${brandName}'s priority retail paths. Watch the weaker priority channels: ${formatList(
    weakerPriorityChannels,
    "lower-visibility retail partners"
  )}.`;

  const recommendedAction = `Focus on practical retail visibility levers: strengthen owned “where to buy” and category pages, improve marketplace and retailer product content where ${brandName} has control or syndication access, and build support content for weak product segments. Priority channels to reinforce: ${formatList(
    priorityChannelNames,
    "priority retail partners"
  )}. Competitors to monitor: ${formatList(
    topCompetitorNames,
    "leading competitors"
  )}.`;

  return {
    status: "ready",
    score,
    headline,
    narrative,
    recommendedAction,
    customerBrandMentionRate,
    priorityRetailerMentionRate,
    ownedChannelMentionRate,
    priorityChannels,
    channelAlternatives,
    topCompetitors,
    productInsights,
  };
}